import { scale, scaleOP, scaleWeb, selectedZoomOPIndex } from '@components/frame';
import { injectConditionalObject, isAndroid, isIOS, isWeb, isMacintosh, isPhone } from '@helpers';

interface FontStyle {
  fontSize: number;
  lineHeight: number;
  fontFamily?: string;
  fontWeight?: 'normal' | 'bold' | '300' | '400' | '500' | '600' | '700';
  letterSpacing?: number;
  textTransform?: 'uppercase' | 'lowercase' | 'capitalize';
}

export interface FontStyles {
  'Large Title': FontStyle;
  'Large Title Normal': FontStyle;
  'Roboto Large Title': FontStyle;
  'Roboto Extra Large Title': FontStyle;
  'Title 1': FontStyle;
  'Title 2': FontStyle;
  'Title 3': FontStyle;
  Headline: FontStyle;
  'Headline Bold': FontStyle;
  'Headline Large': FontStyle;
  'Headline Large Bold': FontStyle;
  Body: FontStyle;
  'Body Large': FontStyle;
  'Body Extra Large': FontStyle;
  'Body Extreme Large': FontStyle;
  'Subhead 1': FontStyle;
  'Subhead 1 bold': FontStyle;
  'Subhead 2': FontStyle;
  'Button 1': FontStyle;
  'Button 2': FontStyle;
  'Button 3': FontStyle;
  Footnote: FontStyle;
  'Caption 1': FontStyle;
  'Caption 2': FontStyle;
  'Caption 3': FontStyle;
  'Caption Rounded': FontStyle;
  Flag: FontStyle;
  Small: FontStyle;
  'Time XXXXL': FontStyle;
  'Time XXXL': FontStyle;
  Time: FontStyle;
  'Time 1': FontStyle;
  'Time 1 bold': FontStyle;
  'Time 2': FontStyle;
  'Time 3': FontStyle;
  'Time 3 bold': FontStyle;
  'Time 3 normal': FontStyle;
  'Time 4': FontStyle;
  'Time 4 normal': FontStyle;
  'Time 6': FontStyle;
  'Mono Body': FontStyle;
  'NC Diff Mono': FontStyle;
  'Counter 1-2 digits': FontStyle;
  'Counter 3 digits': FontStyle;
  'Counter 4 digits': FontStyle;
  'Counter 5 digits': FontStyle;
}

export let font: FontStyles = {};
export let fontOP: FontStyles = {};

const wF = `${isMacintosh ? '' : 'Segoe UI,'} -apple-system, "BlinkMacSystemFont", "Roboto"`;

export const normal = {
  fontFamily: isWeb ? wF : isIOS ? 'SFProDisplay-Regular' : 'Roboto-Regular',
  fontWeight: isWeb ? '300' : 'normal',
};

// Android renders fractional font metrics unreliably: Fabric's sub-pixel text measure vs pixel-grid layout rounding can drop the trailing glyph
const roundFontMetricsToWholePixels = (styles: FontStyles): FontStyles =>
  Object.fromEntries(
    Object.entries(styles).map(([key, style]) => [
      key,
      {
        ...style,
        ...injectConditionalObject(typeof style.fontSize === 'number', { fontSize: Math.round(style.fontSize) }),
        ...injectConditionalObject(typeof style.lineHeight === 'number', { lineHeight: Math.round(style.lineHeight) }),
      },
    ]),
  ) as FontStyles;

export const initFontConfig = (useScaleWeb = false, useScaleOP = false) => {
  const fontFamily = isWeb ? 'web' : isIOS ? 'SFProDisplay' : 'Roboto';
  const bold = {
    fontFamily: isWeb ? wF : isIOS ? 'SFProDisplay-Bold' : 'Roboto-Bold',
    fontWeight: isWeb ? '600' : 'bold',
  };

  const mono = {
    fontFamily: isWeb ? `${isMacintosh ? '' : 'Segoe UI,'} 'SFMono-Regular', 'Roboto Mono'` : isIOS ? 'SFMono-Regular' : 'RobotoMono',
    fontWeight: '400',
  };

  const w500 = {
    fontFamily: isWeb ? wF : isIOS ? 'SFProDisplay-Medium' : 'Roboto-Medium',
    fontWeight: isWeb ? '400' : '500',
  };

  const rounded500 = {
    fontFamily: isWeb ? wF : isIOS ? 'SFProRounded-Medium' : 'Roboto-Medium',
    fontWeight: isWeb ? '400' : '500',
  };

  const time = {
    fontFamily: isWeb ? wF : isIOS ? 'SFProRounded-Regular' : 'Roboto-Regular',
    fontWeight: '400',
  };

  const s = (v) => (useScaleWeb ? scaleWeb(v, false) : useScaleOP ? scaleOP(v) : scale(v, false));

  const base = {
    'Large Title': (() => {
      const common = {
        fontSize: s(37),
        lineHeight: s(36),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0.29),
        },
        SFProDisplay: {
          letterSpacing: s(0.2),
        },
      };

      return {
        ...bold,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'Large Title Normal': (() => {
      const common = {
        fontSize: s(37),
        lineHeight: s(36),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0.29),
        },
        SFProDisplay: {
          letterSpacing: s(0.2),
        },
      };

      return {
        ...normal,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'Roboto Large Title': (() => {
      const common = {
        fontSize: s(32),
        lineHeight: s(42),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0.384),
        },
        SFProDisplay: {
          letterSpacing: s(0.3),
        },
      };

      return {
        ...bold,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'Roboto Extra Large Title': (() => {
      const common = {
        fontSize: s(72),
        lineHeight: s(78),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0.5),
        },
        SFProDisplay: {
          letterSpacing: s(0.4),
        },
      };

      return {
        ...bold,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'Title 1': (() => {
      const common = {
        fontSize: s(26),
        lineHeight: s(31),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.35),
        },
      };

      return {
        ...bold,
        ...common,
        ...setup[fontFamily],
      };
    })(),
    'Title 2': (() => {
      const common = {
        fontSize: s(20),
        lineHeight: s(27),
      };
      const setup = {
        Roboto: {
          fontWeight: '500',
          letterSpacing: s(0),
          fontFamily: 'Roboto-Medium',
        },
        SFProDisplay: {
          fontFamily: 'SFProDisplay-Semibold',
          letterSpacing: s(0.35),
        },
        web: {
          fontWeight: '700',
        },
      };

      return {
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'Title 3': (() => {
      const common = {
        fontSize: s(20),
        lineHeight: s(25),
      };
      const setup = {
        Roboto: {
          fontWeight: '700',
          letterSpacing: s(0),
          fontFamily: 'Roboto-Bold',
        },
        SFProDisplay: {
          fontFamily: 'SFProDisplay-Bold',
          fontWeight: '700',
          letterSpacing: s(0.35),
        },
        web: {
          fontWeight: '700',
        },
      };

      return {
        ...common,
        ...setup[fontFamily],
      };
    })(),

    Headline: (() => {
      const common = {
        fontSize: s(16),
        lineHeight: s(22),
      };
      const setup = {
        Roboto: {
          fontWeight: '500',
          letterSpacing: s(0.3),
          fontFamily: 'Roboto-Medium',
        },
        SFProDisplay: {
          fontFamily: 'SFProDisplay-Semibold',
          fontWeight: '600',
          letterSpacing: s(0.3),
        },
        web: {
          fontWeight: '600',
        },
      };

      return {
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'Headline Bold': (() => {
      const common = {
        fontSize: s(16),
        lineHeight: s(22),
      };
      const setup = {
        Roboto: {
          fontWeight: '600',
          letterSpacing: s(0.3),
          fontFamily: 'Roboto-Bold',
        },
        SFProDisplay: {
          fontFamily: 'SFProDisplay-Bold',
          fontWeight: '600',
          letterSpacing: s(0.3),
        },
        web: {
          fontWeight: '700',
        },
      };

      return {
        ...bold,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'Headline Large': (() => {
      const common = {
        fontSize: s(18),
        lineHeight: s(22),
      };
      const setup = {
        Roboto: {
          fontWeight: '500',
          letterSpacing: s(0.3),
          fontFamily: 'Roboto-Medium',
        },
        SFProDisplay: {
          fontFamily: 'SFProDisplay-Semibold',
          fontWeight: '600',
          letterSpacing: s(0.3),
        },
      };

      return {
        fontWeight: '600',
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'Headline Large Bold': (() => {
      const common = {
        fontSize: s(18),
        lineHeight: s(22),
      };
      const setup = {
        Roboto: {
          fontWeight: '600',
          letterSpacing: s(0.3),
          fontFamily: 'Roboto-Bold',
        },
        SFProDisplay: {
          fontFamily: 'SFProDisplay-Bold',
          fontWeight: '600',
          letterSpacing: s(0.3),
        },
      };

      return {
        ...bold,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    Body: (() => {
      const common = {
        fontSize: s(16),
        lineHeight: s(22),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.4),
        },
      };

      return {
        ...normal,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'Body Large': (() => {
      const common = {
        fontSize: s(18),
        lineHeight: s(22),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.65),
        },
      };

      return {
        ...normal,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'Body Extra Large': (() => {
      const common = {
        fontSize: s(22),
        lineHeight: s(23),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.65),
        },
      };

      return {
        ...normal,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'Body Extreme Large': (() => {
      const common = {
        fontSize: s(30),
        lineHeight: s(42),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.65),
        },
      };

      return {
        ...normal,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'Counter 1-2 digits': (() => {
      const common = {
        fontSize: s(150),
        lineHeight: s(140),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.65),
        },
      };

      return {
        ...normal,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'Counter 3 digits': (() => {
      const common = {
        fontSize: s(120),
        lineHeight: s(140),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.65),
        },
      };

      return {
        ...normal,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'Counter 4 digits': (() => {
      const common = {
        fontSize: s(90),
        lineHeight: s(140),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.65),
        },
      };

      return {
        ...normal,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'Counter 5 digits': (() => {
      const common = {
        fontSize: s(76),
        lineHeight: s(140),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.65),
        },
      };

      return {
        ...normal,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'Subhead 1': (() => {
      const common = {
        fontSize: s(15),
        lineHeight: s(20),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.5),
        },
        web: {
          lineHeight: s(19),
          letterSpacing: s(0.1),
        },
      };

      return {
        ...normal,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'Subhead 1 bold': (() => {
      const common = {
        fontSize: s(15),
        lineHeight: s(20),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.5),
        },
        web: {
          lineHeight: s(19),
          letterSpacing: s(0.1),
        },
      };

      return {
        ...bold,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'Subhead 2': (() => {
      const common = {
        fontSize: s(14),
        lineHeight: s(20),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.5),
        },
      };

      return {
        ...normal,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'Button 1': (() => {
      const common = {
        fontSize: s(15),
        lineHeight: s(20),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.55),
        },
      };

      return {
        ...w500,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'Button 2': (() => {
      const common = {
        fontSize: s(13),
        lineHeight: s(15),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0.3),
        },
        SFProDisplay: {
          letterSpacing: s(0.4),
        },
      };

      return {
        ...w500,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'Button 3': (() => {
      const common = {
        fontSize: s(12),
        lineHeight: s(13),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0.6),
        },
        SFProDisplay: {
          letterSpacing: s(0.9),
        },
      };

      return {
        ...bold,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    Footnote: (() => {
      const common = {
        fontSize: s(13),
        lineHeight: s(18),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0.45),
        },
        SFProDisplay: {
          letterSpacing: s(0.76),
        },
      };

      return {
        ...normal,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'Caption 1': (() => {
      const common = {
        fontSize: s(12),
        lineHeight: s(16),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.4),
        },
      };

      return {
        ...normal,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'Caption 2': (() => {
      const common = {
        fontSize: s(11),
        lineHeight: s(16),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.575),
        },
      };

      return {
        ...normal,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'Caption 3': (() => {
      const common = {
        fontSize: s(13),
        lineHeight: s(18),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0),
        },
      };

      return {
        ...normal,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'Caption Rounded': (() => {
      const common = {
        fontSize: s(12),
        lineHeight: s(16),
      };

      return {
        ...rounded500,
        ...common,
      };
    })(),

    Flag: (() => {
      const common = {
        fontSize: s(10),
        textTransform: 'uppercase',
        lineHeight: s(18),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.15),
        },
      };

      return {
        ...w500,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    Small: (() => {
      const common = {
        fontSize: s(10),
        lineHeight: s(12),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0),
        },
      };

      return {
        ...normal,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    ['Time XXXXL']: (() => {
      const common = {
        fontSize: s(44),
        lineHeight: s(42),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.39),
        },
      };

      return {
        ...time,
        fontWeight: isWeb ? '600' : 'bold',
        ...common,
        ...setup[fontFamily],
      };
    })(),

    ['Time XXXL']: (() => {
      const common = {
        fontSize: s(32),
        lineHeight: s(32),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.33),
        },
      };

      return {
        ...time,
        fontWeight: isWeb ? '600' : 'bold',
        ...common,
        ...setup[fontFamily],
      };
    })(),

    Time: (() => {
      const common = {
        fontSize: s(21),
        lineHeight: s(20),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.42),
        },
      };

      return {
        ...time,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    ['Time 1']: (() => {
      const common = {
        fontSize: s(21),
        lineHeight: s(21),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.84),
        },
      };

      return {
        ...time,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    ['Time 1 bold']: (() => {
      const common = {
        fontSize: s(21),
        lineHeight: s(21),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.84),
        },
      };

      return {
        ...bold,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    ['Time 2']: (() => {
      const common = {
        fontSize: s(18),
        lineHeight: s(22),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.72),
        },
      };

      return {
        ...time,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    ['Time 3']: (() => {
      const common = {
        fontSize: s(16),
        lineHeight: s(20),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.64),
        },
      };

      return {
        ...time,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    ['Time 3 bold']: (() => {
      const common = {
        fontSize: s(16),
        lineHeight: s(20),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.64),
        },
      };

      return {
        ...bold,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    ['Time 3 normal']: (() => {
      const common = {
        fontSize: s(16),
        lineHeight: s(20),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.64),
        },
      };

      return {
        ...normal,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    ['Time 4']: (() => {
      const common = {
        fontSize: s(14),
        lineHeight: s(20),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.56),
        },
      };

      return {
        ...time,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    ['Time 4 normal']: (() => {
      const common = {
        fontSize: s(14),
        lineHeight: s(20),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.56),
        },
      };

      return {
        ...normal,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    ['Time 6']: (() => {
      const common = {
        fontSize: s(11),
        lineHeight: s(16),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0.44),
        },
      };

      return {
        ...time,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'Mono Body': (() => {
      let common;
      let setup;

      if (isPhone) {
        common = {
          fontSize: s(12),
          lineHeight: s(25),
        };
        setup = {
          Roboto: {
            letterSpacing: s(0.15),
          },
          SFProDisplay: {
            letterSpacing: s(0.05),
          },
        };
      } else {
        common = {
          fontSize: s(15),
        };
        setup = {
          Roboto: {
            letterSpacing: s(0.6),
            lineHeight: s(30),
          },
          SFProDisplay: {
            letterSpacing: s(0.3),
            lineHeight: s(29),
          },
          web: {
            letterSpacing: s(0.3),
            lineHeight: s(29),
          },
        };
      }

      return {
        ...mono,
        ...common,
        ...setup[fontFamily],
      };
    })(),

    'NC Diff Mono': (() => {
      const common = {
        fontSize: s(16),
        lineHeight: s(22),
      };
      const setup = {
        Roboto: {
          letterSpacing: s(0),
        },
        SFProDisplay: {
          letterSpacing: s(0),
        },
        web: {
          letterSpacing: s(0),
        },
      };

      return {
        fontFamily: isWeb ? "'Roboto Mono', 'SFMono-Regular', monospace" : 'RobotoMono',
        fontWeight: '400',
        ...common,
        ...setup[fontFamily],
      };
    })(),
  };

  // Android only — web and iOS keep the original fractional metrics untouched
  const platformBase = isAndroid ? roundFontMetricsToWholePixels(base) : base;

  if (useScaleOP) {
    fontOP = platformBase;
    return;
  }

  if (useScaleWeb) {
    return platformBase;
  }

  font = platformBase;
};
